import React, { useState } from 'react';
import { Modal, Input, Button, Upload, ConfigProvider, message } from 'antd';
import { ImagePlus, X as CloseIcon, HelpCircle } from 'lucide-react';
import type { RcFile } from 'antd/es/upload';

const BRAND = '#066BFF';

const toBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, imageUrl?: string) => void;
}

export const PostUpdateModal: React.FC<Props> = ({ open, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState<string | undefined>();

  const submit = () => {
    if (!content.trim()) {
      message.error('Nhập nội dung cập nhật');
      return;
    }
    onSubmit(content.trim(), imageUrl);
    setContent('');
    setImageUrl(undefined);
    onClose();
  };

  return (
    <ConfigProvider
      theme={{ token: { fontFamily: "'Be Vietnam Pro', inter, sans-serif" } }}
    >
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={520}
        centered
        closeIcon={<CloseIcon size={20} className="text-slate-400" />}
        styles={{ content: { borderRadius: 20, padding: 24 }, body: { paddingTop: 8 } }}
      >
        <h2 className="text-center text-[24px] font-semibold leading-8 tracking-tight mb-5">
          Đăng bài
        </h2>

        <div className="flex flex-col gap-2">
          <span className="text-[14px] font-medium text-[#212b4e]">Nhập nội dung</span>
          <div className="relative">
            <Input.TextArea
              className="!rounded-[12px]"
              rows={5}
              maxLength={2000}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Chia sẻ tiến độ, hoạt động mới của chiến dịch..."
              style={{ resize: 'vertical' }}
            />
            <HelpCircle
              size={16}
              className="text-slate-300 absolute top-3 right-3 pointer-events-none"
            />
          </div>
        </div>

        <div className="mt-4">
          <Upload
            accept="image/*,video/*"
            showUploadList={false}
            className="w-full [&_.ant-upload]:!w-full [&_.ant-upload-select]:!w-full"
            beforeUpload={async (file) => {
              if (file.size / 1024 / 1024 > 10) {
                message.error('Mỗi ảnh không quá 10mb');
                return Upload.LIST_IGNORE;
              }
              setImageUrl(await toBase64(file));
              return false;
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="preview"
                className="w-full max-h-[180px] object-cover rounded-[8px] border border-[#d1cfd2]"
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 h-[120px] w-full rounded-[8px] border border-dashed border-[#d1cfd2] cursor-pointer hover:border-[#066BFF] transition-colors">
                <ImagePlus size={24} className="text-[#066BFF]" />
                <span className="text-[14px]" style={{ color: BRAND }}>
                  Upload ảnh/video
                </span>
                <span
                  className="text-[12px] text-center text-[#51545a] leading-snug"
                  style={{ fontFamily: "'Quicksand', sans-serif" }}
                >
                  (Định dạng ảnh .jpg, .png
                  <br />
                  Mỗi ảnh không quá 10mb)
                </span>
              </div>
            )}
          </Upload>
        </div>

        <div className="flex justify-center mt-5">
          <Button
            type="primary"
            size="large"
            onClick={submit}
            className="!rounded-full !px-8"
            style={{ background: BRAND, borderColor: BRAND, fontFamily: 'inter, sans-serif' }}
          >
            Xác nhận
          </Button>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default PostUpdateModal;
